export type DropboxEmbedder = {
  embed: (options: {link: string}, element: HTMLElement) => unknown;
  unmount: (embed: unknown) => void;
  isBrowserSupported: () => boolean;
};

type DropboxWindow = Window & {Dropbox?: DropboxEmbedder};

let pendingLoad: {appKey: string; promise: Promise<DropboxEmbedder>} | undefined;

export const loadDropboxEmbedder = (appKey: string): Promise<DropboxEmbedder> => {
  if (pendingLoad) {
    return pendingLoad.appKey === appKey
      ? pendingLoad.promise
      : Promise.reject(new Error('Dropbox embedder already uses another app key'));
  }

  const existingScript = document.getElementById('dropboxjs') as HTMLScriptElement | null;
  if (existingScript && existingScript.dataset.appKey !== appKey) {
    return Promise.reject(new Error('Dropbox embedder already uses another app key'));
  }
  const script = existingScript ?? document.createElement('script');
  if (!existingScript) {
    script.id = 'dropboxjs';
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    script.dataset.appKey = appKey;
    script.async = true;
  }

  const promise = new Promise<DropboxEmbedder>((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
    const fail = () => {
      cleanup();
      script.remove();
      reject(new Error('Dropbox embedder failed to load'));
    };
    const onLoad = () => {
      const sdk = (window as DropboxWindow).Dropbox;
      if (!sdk?.embed || !sdk.unmount || !sdk.isBrowserSupported) {
        fail();
        return;
      }
      cleanup();
      resolve(sdk);
    };
    const onError = () => fail();
    const timeout = window.setTimeout(fail, 15000);
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (existingScript && (window as DropboxWindow).Dropbox) {
      onLoad();
    } else if (!existingScript) {
      document.head.appendChild(script);
    }
  });
  pendingLoad = {appKey, promise};
  void promise.catch(() => {
    pendingLoad = undefined;
  });
  return promise;
};
